/**
 * Sequencer for the hero's product-video background.
 *
 * Plays one clip, and as it nears its end cross-fades into the next. Two
 * <video> slots alternate: whichever one is on screen is playing, the other is
 * buffering the clip after it. Swapping the src on a single element would blank
 * the hero for as long as the next clip took to buffer, which on a hero is the
 * whole point of failure.
 *
 * Deliberately dependency-free rather than extending @theme/component: it needs
 * no ref plumbing, and staying off the import map means the hero keeps working
 * if this is lifted into another theme.
 */

/** How long the cross-fade runs. Must match --cap-hero-fade in the snippet. */
const FADE_MS = 700;

/**
 * Start the hand-off this many seconds before the clip ends, so the dissolve
 * happens over moving footage. Waiting for 'ended' would fade out of a frozen
 * final frame.
 */
const HANDOFF_LEAD = 0.8;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class CapHeroVideoPlaylist extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('script[data-cap-hero-clips]');
    this.videos = Array.from(this.querySelectorAll('video'));
    this.toggle = this.querySelector('[data-cap-hero-toggle]');

    try {
      this.clips = JSON.parse(payload?.textContent ?? '[]');
    } catch {
      this.clips = [];
    }

    if (this.clips.length === 0 || this.videos.length < 2) return;

    /** Index into this.videos of the slot currently on screen. */
    this.active = 0;
    this.handingOff = false;
    this.userPaused = false;
    /**
     * Set when the visitor presses play themselves. Without it, someone on
     * prefers-reduced-motion who deliberately starts the hero would have it
     * silently refuse to come back every time they scrolled past and returned,
     * because the resume path also honours that preference.
     */
    this.userStarted = false;
    /**
     * Preference order for the two renditions of each clip. Flipped once if the
     * preferred format fails to decode - source selection is one-shot, so a
     * format that dies mid-clip has no automatic second chance. Held on the
     * instance because sources are rebuilt from the clip list on every load,
     * which would otherwise reinstate the broken format on the next clip.
     */
    this.sourceOrder = ['mp4', 'hls'];
    this.sourceOrderFlipped = false;

    for (const video of this.videos) {
      video.addEventListener('timeupdate', () => this.#maybeHandOff(video));
      video.addEventListener('ended', () => this.#handOffFrom(video));
      // Capture phase so this also sees error events from the <source> children.
      video.addEventListener('error', () => this.#onMediaError(video), true);
    }

    this.videos[0].addEventListener('playing', () => this.#primeStandby(), { once: true });
    this.toggle?.addEventListener('click', () => this.#onToggleClick());

    // A hero scrolled off the top of the page is still decoding frames unless
    // something stops it.
    this.visibility = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#resume();
        else this.#pause();
      }
    });
    this.visibility.observe(this);

    this.onTabVisibility = () => {
      if (document.hidden) this.#pause();
      else this.#resume();
    };
    document.addEventListener('visibilitychange', this.onTabVisibility);

    this.onMotionChange = () => {
      if (reducedMotion.matches) this.#pause();
    };
    reducedMotion.addEventListener('change', this.onMotionChange);

    if (this.#autoplayWanted()) {
      this.#play();
    } else {
      // Left on the poster deliberately. The toggle already reads "play", so
      // there is a way in for anyone who wants the motion after all.
      this.setAttribute('data-idle', 'true');
    }
  }

  disconnectedCallback() {
    this.visibility?.disconnect();
    if (this.onTabVisibility) document.removeEventListener('visibilitychange', this.onTabVisibility);
    if (this.onMotionChange) reducedMotion.removeEventListener('change', this.onMotionChange);
  }

  /**
   * Motion that starts by itself is unwelcome under prefers-reduced-motion, and
   * a hero video is a poor use of a metered connection.
   */
  #autoplayWanted() {
    if (reducedMotion.matches) return false;
    if (navigator.connection?.saveData) return false;
    return true;
  }

  #current() {
    return this.videos[this.active];
  }

  #standby() {
    return this.videos[1 - this.active];
  }

  #clipIndexOf(video) {
    const raw = Number(video.dataset.clip);
    return Number.isInteger(raw) ? raw : 0;
  }

  /** Load the clip after the one now playing into the off-screen slot. */
  #primeStandby() {
    if (this.clips.length < 2) return;
    const next = (this.#clipIndexOf(this.#current()) + 1) % this.clips.length;
    this.#loadClip(this.#standby(), next);
  }

  #loadClip(video, index) {
    const clip = this.clips[index];
    if (!clip) return;

    video.dataset.clip = String(index);
    video.replaceChildren();

    // Order matters: a browser takes the first source it can play, not the best
    // one. See the snippet for why the hero prefers mp4 while the product
    // carousel prefers HLS - short version, HLS opens on its lowest rendition
    // and never climbs across clips this short, which a full-bleed hero shows.
    for (const format of this.sourceOrder) {
      if (format === 'mp4' && clip.mp4) {
        video.appendChild(this.#source(clip.mp4, 'video/mp4'));
      }
      if (format === 'hls' && clip.hls) {
        video.appendChild(this.#source(clip.hls, 'application/x-mpegURL'));
      }
    }

    if (clip.poster) video.poster = clip.poster;
    video.preload = 'auto';
    video.load();
  }

  #source(src, type) {
    const source = document.createElement('source');
    source.src = src;
    source.type = type;
    return source;
  }

  #maybeHandOff(video) {
    if (video !== this.#current() || this.handingOff) return;

    const { duration, currentTime } = video;
    // HLS reports Infinity until the manifest is parsed; the 'ended' listener
    // covers that case.
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (duration - currentTime > HANDOFF_LEAD) return;

    this.#handOffFrom(video);
  }

  #handOffFrom(video) {
    if (video !== this.#current() || this.handingOff) return;

    // A single clip has nowhere to hand off to, so loop it in place.
    if (this.clips.length < 2) {
      video.currentTime = 0;
      video.play().catch(() => {});
      return;
    }

    const outgoing = this.#current();
    const incoming = this.#standby();

    this.handingOff = true;

    incoming.classList.add('is-active');
    outgoing.classList.remove('is-active');
    incoming.play().catch(() => {});
    this.active = 1 - this.active;

    // Only once the fade has finished - stopping the outgoing clip mid-dissolve
    // freezes a frame in plain view.
    const followUp = (this.#clipIndexOf(incoming) + 1) % this.clips.length;
    window.setTimeout(() => {
      outgoing.pause();
      this.#loadClip(outgoing, followUp);
      this.handingOff = false;
    }, FADE_MS);
  }

  /**
   * Source selection is one-shot, so a rendition that the browser accepts and
   * then fails to decode would leave a dead hero with no second chance. Flip
   * the preference once and reload this slot on the other format.
   */
  #onMediaError(video) {
    if (this.sourceOrderFlipped) return;

    this.sourceOrderFlipped = true;
    this.sourceOrder = this.sourceOrder.slice().reverse();

    const wasActive = video === this.#current();
    this.#loadClip(video, this.#clipIndexOf(video));
    if (wasActive && !this.userPaused) this.#play();
  }

  #onToggleClick() {
    if (this.userPaused || this.#current().paused) {
      this.userPaused = false;
      this.userStarted = true;
      this.removeAttribute('data-idle');
      this.#play();
    } else {
      this.userPaused = true;
      this.#pause();
    }
  }

  /**
   * Restart after the hero scrolled back into view or the tab came forward.
   * Distinct from #play because it must not override either an explicit pause
   * or a standing reduced-motion / Save-Data preference.
   */
  #resume() {
    if (this.userPaused) return;
    if (!this.#autoplayWanted() && !this.userStarted) return;
    this.#play();
  }

  #play() {
    const video = this.#current();
    // Autoplay gets refused often enough - low power mode, a lost muted
    // attribute - that an unhandled rejection here would be routine noise.
    video.play().then(
      () => this.#paintToggle(true),
      () => this.#paintToggle(false)
    );
  }

  #pause() {
    for (const video of this.videos) {
      if (!video.paused) video.pause();
    }
    this.#paintToggle(false);
  }

  #paintToggle(playing) {
    if (!this.toggle) return;

    this.toggle.setAttribute(
      'aria-label',
      playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay
    );
    this.toggle.querySelector('.cap-hero-playlist__icon--pause')?.classList.toggle('hidden', !playing);
    this.toggle.querySelector('.cap-hero-playlist__icon--play')?.classList.toggle('hidden', playing);
  }
}

if (!customElements.get('cap-hero-video-playlist')) {
  customElements.define('cap-hero-video-playlist', CapHeroVideoPlaylist);
}
